import ReactGA from 'react-ga4';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MdOutlineContentCopy } from 'react-icons/md';
import { TfiReload } from 'react-icons/tfi';
import { GiNinjaHead } from 'react-icons/gi';
////////////////////////////////////////////////////////////////////
import { telegramWebApp } from '@/main';
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import {
	InviteFriendCard,
	PageTitle,
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
	FriendsList,
	LevelsInfo,
	Button,
} from '@/app/shared/components';
import { setAmountFriends, setFriends } from '@/core/redux/slices';
import { FriendsService } from '@/core/services';

/**
 * The Friends page component.
 *
 * This component renders the Friends page, which displays information about
 * the user's friends, including their names, levels, and rewards. It also allows
 * the user to invite friends to the app and view their rewards.
 *
 * @returns The Friends page component.
 */
const Friends: React.FC = () => {
	/*********************
	// #region Hooks
	**********************/
	const dispatch = useCustomDispatch();
	const { t } = useTranslation();

	/*********************
	// #region Redux
	**********************/
	const { newFriendsReward } = useCustomSelector((state) => state.counter);
	const { friendsAmount } = useCustomSelector((state) => state.friends);
	const { id } = useCustomSelector((state) => state.user.data);

	/*********************
	// #region Observables
	**********************/
	useEffect(() => {
		ReactGA.send({
			hitType: 'pageview',
			page: '/friends',
			title: 'Friends',
		});
	}, []);

	/*********************
	// #region States
	**********************/
	const [isLoaded, setIsLoaded] = useState<boolean>(true);

	/*********************
	// #region Functions  
	**********************/
	const getFriends = async () => {
		setIsLoaded(false);
		try {
			const response = await FriendsService.getFriends();

			dispatch(setFriends(response));
			dispatch(setAmountFriends(response.length));

			setIsLoaded(true);
		} catch (error: any) {
			let errorMessage = t('general.cannotConnect');

			if (error.response) {
				errorMessage += ` Server responded with status ${
					error.response.status
				}: ${error.response.data?.message || error.response.data}`;
			} else if (error.request) {
				errorMessage += ' No response received from server.';
			} else {
				errorMessage += ` ${error.message}`;
			}
		}
	};

	const copyInviteToClipboard = () => {
		navigator.clipboard
			.writeText(
				`${process.env.VITE_INVITE_URL}${process.env.VITE_INVITE_PARAM_URL}${id}`
			)
			.then(() => {
				toast.success(t('friends.copiedToClipboard'));
			})
			.catch((err: any) => {
				console.error(err);
				toast.error(t('friends.copiedToClipboardError'));
			});
	};

	/*********************
	// #region Template
	**********************/
	return (
		<div className="flex flex-col gap-2 pt-8 pb-52 mx-4">
			<div className="flex flex-col items-center">
				<PageTitle>{t('friends.title')}</PageTitle>
			</div>
			<p className="text-sm text-center">{t('friends.description')}</p>

			<div className="flex gap-3 mt-6">
				<div
					className="flex flex-1"
					onClick={() => {
						telegramWebApp.HapticFeedback.impactOccurred('heavy');
						telegramWebApp.openTelegramLink(
							`https://t.me/share/url?url=${process.env.VITE_INVITE_URL}${process.env.VITE_INVITE_PARAM_URL}${id}`
						);
					}}
				>
					<InviteFriendCard
						title={t('friends.inviteFriend')}
						image={process.env.VITE_INVITE_FRIEND_IMAGE!}
						gradient={'bg-invite-one-gradient'}
						// text={t('friends.inviteFriendDescription')}
						shurikens={newFriendsReward}
					/>
				</div>
				<div
					className="flex flex-1"
					onClick={() => {
						telegramWebApp.HapticFeedback.impactOccurred('heavy');
						telegramWebApp.openTelegramLink(
							`https://t.me/share/url?url=${process.env.VITE_INVITE_URL}${process.env.VITE_INVITE_PARAM_URL}${id}`
						);
					}}
				>
					<InviteFriendCard
						title={t('friends.invite5Friends')}
						image={process.env.VITE_INVITE_FIVE_FRIENDS_IMAGE!}
						gradient={'bg-invite-multiple-gradient'}
						// text={t('friends.invite5FriendsDescription')}
						shurikens={newFriendsReward * 5}
						// shurikens={20000}
					/>
				</div>
			</div>

			<Accordion type="single" collapsible className="mt-3">
				<AccordionItem value="item-1" className="border-none">
					<AccordionTrigger className="flex rounded-2xl items-center bg-selector-one-gradient border-0 p-2 option-menu-setting shadow-lg shadow-black/25 hover:no-underline">
						<div className="flex items-center flex-1 gap-4 pl-3">
							<img
								src={process.env.VITE_GIFT_IMAGE}
								alt={t('friends.moreBonuses')}
								className="h-[61px] w-[61px]"
							/>
							<p className="text-base font-semibold">
								{t('friends.moreBonuses')}
							</p>
						</div>
					</AccordionTrigger>
					<AccordionContent>
						<LevelsInfo />
					</AccordionContent>
				</AccordionItem>
			</Accordion>

			<div className="mt-4">
				<div className="flex justify-between items-center w-full">
					<p>
						{t('friends.listOfFriends')} ({friendsAmount})
					</p>

					<div
						className={`${!isLoaded ? 'animate-spin' : ''} `}
						onClick={getFriends}
					>
						<TfiReload />
					</div>
				</div>

				{isLoaded && <FriendsList />}
			</div>

			<div className="flex gap-2 items-center fixed w-screen max-w-6xl py-4 bottom-28 px-4 left-0 right-0">
				<Button
					variant={'secondary'}
					onClick={() => {
						telegramWebApp.HapticFeedback.impactOccurred('heavy');
						telegramWebApp.openTelegramLink(
							`https://t.me/share/url?url=${process.env.VITE_INVITE_URL}${process.env.VITE_INVITE_PARAM_URL}${id}`
						);
					}}
				>
					{t('friends.inviteFriend')}

					{process.env.VITE_APP_NAME === 'Vottun Dojo' && (
						<GiNinjaHead />
					)}
				</Button>
				<Button
					variant={'secondary'}
					size={'icon'}
					onClick={copyInviteToClipboard}
				>
					<MdOutlineContentCopy />
				</Button>
			</div>
		</div>
	);
};

export default Friends;
